<template>
  <div class="product-images-wrapper desk-hor-mob-col">
    <div
      ref="allImages"
      :class="['all-images-container']"
      tabindex="0"
      @keydown.right="nextImgClick()"
      @keydown.left="prevImgClick()"
    >
      <!-- DESK ONLY -->
      <template v-if="deviceStore?.device?.isDesktop">
        <div
          v-for="(image, index) in slicedImages"
          :key="`${index}_image_desk_${image?.public_id}`"
          role="button"
          :aria-label="`View ${image.name} Image`"
          :class="[
            'small-image-container pointer',
            { current: index === state.currentImageIndex },
            { 'is-video': checkIfVideo(image) },
          ]"
          @click="setCurrentIndex(index)"
          @mouseover="setHoverCurrentImage(index)"
          @mouseleave="state.hoverCurrentIndex = 0"
        >
          <Video
            v-if="checkIfVideo(image)"
            :preload="true"
            :asset="image"
            :options="videoOptions"
            :display_as_gif="true"
            :video-classes-override="['small-video']"
          />
          <CloudinaryImage
            v-else
            :public-id="image?.public_id"
            :options="imageOptions"
            :alt="useImgAlt(image.name || productName)"
            :image-classes-override="['small-image']"
          />
        </div>
      </template>
      <!-- TAB/MOB only -->
      <template v-else>
        <div
          v-for="(image, index) in props.images"
          :key="`${index}_image_mob_${image?.public_id}`"
          role="button"
          :aria-label="`View ${image.name} Image`"
          :class="[
            'small-image-container pointer tab-mob-only-flex',
            { current: index === state.currentImageIndex },
            { 'is-video': checkIfVideo(image) },
          ]"
          @click="setCurrentIndex(index)"
          @mouseover="setHoverCurrentImage(index)"
          @mouseleave="state.hoverCurrentIndex = 0"
        >
          <Video
            v-if="checkIfVideo(image)"
            :preload="true"
            :poster="cloudinary.generateVideoUrl(image?.public_id + '.jpg', { width: 85, height: 85, thumbnail: true })"
            :asset="image"
            :options="{ width: 85, height: 85, quality: 'auto:eco' }"
            :display_as_gif="true"
            :video-classes-override="['small-image']"
          />
          <CloudinaryImage
            v-else
            :public-id="image?.public_id"
            :options="{ width: 85, height: 85, quality: 'auto:eco' }"
            :alt="useImgAlt(image.name || productName)"
            :image-classes-override="['small-image']"
          />
        </div>
      </template>
      <CustomButton
        v-if="numImages > 6"
        class="desk-only-flex"
        style-name="secondary"
        style-color="black"
        :is-small="true"
        @click-button="showOrHideMoreImages()"
      >
        {{ buttonText }}
      </CustomButton>
    </div>

    <div ref="currentImageRef" class="current-image-container">
      <div class="button-container flex-inline between">
        <CustomButton
          :icon-only="true"
          style-name="tertiary"
          style-color="black"
          aria-label="Previous Product Image"
          @click-button="prevImgClick()"
        >
          <ArrowLeftSLine />
        </CustomButton>
        <CustomButton
          :icon-only="true"
          style-name="tertiary"
          style-color="black"
          aria-label="Next Product Image"
          @click-button="nextImgClick()"
        >
          <ArrowRightSLine />
        </CustomButton>
      </div>
      <Video
        v-if="currentImage && checkIfVideo(currentImage)"
        :preload="true"
        :poster="
          cloudinary.generateVideoUrl(currentImage?.public_id + '.jpg', {
            ...videoOptions,
            thumbnail: true,
            quality: 'auto:eco',
          })
        "
        :asset="currentImage"
        :options="videoOptions"
        :display_as_gif="true"
        :video-classes-override="['current-image']"
        :video-container-classes="['current-image']"
      />
      <CloudinaryImage
        v-else-if="currentImage"
        :public-id="currentImage?.public_id"
        :options="imageOptions"
        :alt="useImgAlt(currentImage.name || productName)"
        :image-classes-override="['current-image']"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { handleTouchStart, handleTouchMove } from '../util/eventHandler';
import { AssetCloudinary } from '~/types/contentstack';
import { ImageOptions, useCloudinary, VideoOptions } from '~/composables/cloudinary';
import { useDeviceStore } from '~/store/device';

const deviceStore = useDeviceStore();
const cloudinary = useCloudinary();
const currentImageRef = ref<HTMLElement | null>(null);

const props = defineProps({
  images: {
    type: Array as () => AssetCloudinary[],
    required: true,
  },
  productName: {
    type: String,
    default: '',
  },
});

const state = reactive({
  currentImageIndex: 0,
  hoverCurrentIndex: 0,
  mobSizes: [350, 400, 450, 767],
  baseDisplayImages: 6,
});

// TODO - check for variant change and reset current selected index to 0 in case the # images don't match

const numImages = computed(() => props.images?.length || 0);
const currentImage = computed(() => {
  if (numImages.value) {
    if (state.hoverCurrentIndex) {
      return props.images[state.hoverCurrentIndex];
    }
    return props.images[state.currentImageIndex];
  }
  return undefined;
});
const imageOptions = computed<ImageOptions>(() => {
  return {
    width: deviceStore.isMobile ? 480 : deviceStore.isDesktop ? 405 : 550,
    height: deviceStore.isMobile ? 480 : deviceStore.isDesktop ? 405 : 550,
    quality: deviceStore.isMobile || deviceStore.isTablet ? 'auto' : 'auto',
  };
});

const videoOptions = computed<VideoOptions>(() => {
  return {
    width: deviceStore.isMobile ? 480 : deviceStore.isDesktop ? 405 : 550,
    height: deviceStore.isMobile ? 480 : deviceStore.isDesktop ? 405 : 550,
    quality: deviceStore.isMobile || deviceStore.isTablet ? 'auto' : 'auto',
  };
});

const buttonText = computed(() => (state.baseDisplayImages >= numImages?.value ? 'See Less' : 'See More'));
const slicedImages = computed(() => {
  return props.images.slice(0, state.baseDisplayImages);
});

onMounted(() => {
  if (currentImageRef) {
    currentImageRef.value?.addEventListener('touchstart', (ev: any) => touchStartHandler(ev));
    currentImageRef.value?.addEventListener('touchmove', (ev: any) => swipeHandler(ev));
  }
});
onUnmounted(() => {
  if (currentImageRef) {
    // currentImageRef.value?.removeEventListener('touchstart', (ev: any) => touchStartHandler(ev));
    currentImageRef.value?.removeEventListener('touchmove', (ev: any) => swipeHandler(ev));
  }
});

function showOrHideMoreImages(): void {
  return state.baseDisplayImages >= numImages?.value
    ? (state.baseDisplayImages = 6)
    : (state.baseDisplayImages = numImages?.value);
}
function checkIfVideo(image: AssetCloudinary): boolean {
  return image?.resource_type === 'video';
}
function setCurrentIndex(index: number): void {
  state.currentImageIndex = index;
}
function setHoverCurrentImage(index: number): void {
  state.hoverCurrentIndex = index;
}

function touchStartHandler(ev: any) {
  handleTouchStart(ev);
}
function swipeHandler(ev: any) {
  const swipeType = handleTouchMove(ev);
  if (swipeType === 'left') {
    prevImgClick();
  } else if (swipeType === 'right') {
    nextImgClick();
  }
}
function prevImgClick(): void {
  if (state.currentImageIndex !== 0) {
    setCurrentIndex(state.currentImageIndex - 1);
  } else if (numImages.value) {
    // set to last image
    setCurrentIndex(numImages.value - 1);
  }
}
function nextImgClick(): void {
  if (numImages.value && state.currentImageIndex !== numImages.value - 1) {
    setCurrentIndex(state.currentImageIndex + 1);
  } else {
    // set to first image
    setCurrentIndex(0);
  }
}
</script>

<style lang="scss" scoped>
$desktop-width: #{local-functions.rem-calc(650px)};
$desktop-height: #{local-functions.rem-calc(650px)};

.small-image-container {
  display: flex;
  &.current {
    :deep(.small-image) {
      border: 2px solid $color-primary-500;
    }
  }
  &.is-video {
    &::before {
      content: '';
      display: inline-block;
      z-index: map.get(local-vars.$zindex, 'page');
      position: relative;
      top: 42px;
      left: 16px;
      margin-left: -16px; // offset
      @include local-mixins.backgroundMaskImage(
        local-functions.svg-img-source('play-fill.svg'),
        $color-neutral-white,
        16px,
        16px
      );
      @include local-mixins.desktop {
        top: 66px;
      }
    }
  }
  :deep(.small-image),
  :deep(.video-container) {
    border-radius: 4px;
    object-fit: cover;
    height: 85px;
    width: 85px;
    background-color: #f7f7f7;
  }
  + .small-image-container {
    margin-top: 1rem;
  }
}
.current-image-container {
  display: flex;
  position: relative;
  @include local-mixins.desktop {
    display: none;
  }

  :deep(.current-image) {
    object-fit: cover;
    height: $desktop-height;
    width: $desktop-width;
    // background-color: #f7f7f7;
  }
  .button-container {
    position: absolute;
    z-index: map.get(local-vars.$zindex, 'page');
    right: 0;
    left: 0;
    // top: 0;
    top: calc(50% - 25px);
    height: fit-content;
    bottom: 0;
    padding-left: 1.5rem;
    padding-right: 1.5rem;
    .btn:deep() {
      background-color: rgba(255, 255, 255, 0.8);
      height: #{local-functions.rem-calc(50)};
      width: #{local-functions.rem-calc(50)};
      border-radius: 50%;
      border: none;
      padding: 0;
      :deep(svg) {
        fill: $color-neutral-black;
        margin: 0;
        height: 32px;
        width: 37px;
      }
    }
    @include local-mixins.tablet_and_mobile {
      padding-left: 1rem;
      padding-right: 1rem;
      top: calc(50% - 18px);
      .btn:deep() {
        height: #{local-functions.rem-calc(36)};
        width: #{local-functions.rem-calc(36)};
        :deep(svg) {
          fill: $color-neutral-black;
          margin: 0;
          height: 26px;
          width: 29px;
        }
      }
    }
  }
}
.all-images-container {
  margin-right: #{local-functions.rem-calc(30px)};
}
@include local-mixins.desktop {
  .product-images-wrapper {
    padding: 0 !important;
    .all-images-container {
      max-height: fit-content;
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 1rem;
      overflow-y: unset;
      flex-direction: column;

      /* Hide scrollbar for IE, Edge and Firefox */
      -ms-overflow-style: none; /* IE and Edge */
      scrollbar-width: none; /* Firefox */

      button {
        grid-column: span 2;
        margin: 0 auto;
      }
      .small-image-container {
        gap: 1rem;
        margin-top: 0;
        &.current {
          :deep(.small-image),
          :deep(.video-container) {
            border: 0;
          }
        }
        &.is-video {
          &::before {
            content: none;
          }
        }
        // &:nth-child(odd) + button {
        //   grid-column: span 2;
        // }
        :deep(.small-image),
        :deep(.video-container) {
          height: 405px;
          width: 405px;
          @include local-mixins.xxl {
            height: 375px;
            width: 375px;
          }
        }
      }

      /* Hide scrollbar for Chrome, Safari and Opera */
      &::-webkit-scrollbar {
        display: none;
      }
    }
    .current-image-container {
      :deep(.current-image) {
        border-radius: 8px;
      }
    }
  }
}
@include local-mixins.tablet_and_mobile {
  .product-images-wrapper {
    padding: 0 !important;
    margin-bottom: 1rem;
    .all-images-container {
      order: 1;
      display: flex;
      flex-direction: row;
      overflow-x: auto;
      overflow-y: hidden;
      margin-top: 1rem;
      margin-right: 0;
      .small-image-container {
        :deep(.video-container),
        :deep(.small-image) {
          height: 60px;
          width: 60px;
        }
        + .small-image-container {
          margin-top: 0;
          margin-left: 0.5rem;
        }
      }
    }

    .current-image-container {
      order: 0;
      // square mobile image is what is mocked for now
      :deep(.current-image) {
        height: 100vw;
        width: 100%;
        margin: 0 auto;
      }
    }
  }
}
@include local-mixins.tablet {
  .product-images-wrapper {
    width: 100%;
    .current-image-container {
      width: 100%;
      justify-content: center;
      :deep(.current-image) {
        margin: 0 auto;
        height: auto;
        max-width: #{local-functions.rem-calc(550)}; // TODO - just a random number for now
        min-height: #{local-functions.rem-calc(550)};
      }
    }
  }
}
</style>
